/**
 * Visual Config — Builder functions.
 *
 * `buildEditorState()`  — construct a ChartEditorState from DB/API data.
 * `toPersistedConfig()` — extract persistence-ready objects (delta-save).
 */
import type {
  ChartTypeOption,
  TypeStyle,
  ChartStyleState,
  ChartFieldsConfig,
  ChartEditorState,
  LabelPosition,
  SortOption,
} from './types';
import {
  DEFAULT_EDITOR_STATE,
  DEFAULT_CHART_STYLE,
  DEFAULT_TYPE_STYLES,
  DEFAULT_SORT_LIMIT,
  getChartStyleDefaults,
} from './defaults';
import { getDefaultLabelPosition } from './chartTypes';

/**
 * Build a `ChartEditorState` from a chart's DB/API data.
 *
 * Merges stored values over defaults so missing fields get sensible values.
 *
 * @param chartType        — the chart's visualization type
 * @param chartStyleRaw    — the `chart_style` DB column (visual presentation)
 * @param chartFieldsRaw   — the `chart_fields_config` DB column (sort/limit)
 */
export function buildEditorState(
  chartType: string,
  chartStyleRaw: Record<string, unknown> | null | undefined,
  chartFieldsRaw?: Record<string, unknown> | null,
): ChartEditorState {
  const ct = (chartType || DEFAULT_EDITOR_STATE.chartType) as ChartTypeOption;
  const stored = (chartStyleRaw ?? {}) as Record<string, unknown>;
  const storedFields = (chartFieldsRaw ?? {}) as Record<string, unknown>;
  const sortLimitDefaults = DEFAULT_SORT_LIMIT[ct] ?? DEFAULT_SORT_LIMIT.bar;

  // ── Resolve chart fields config (sort/limit) ──────────────────────────
  // Note: DB/migration may store explicit JSON null for absent keys.
  // Treat null the same as undefined (fall through to defaults).
  const resolvedSort: SortOption = storedFields.sort != null
    ? storedFields.sort as SortOption
    : sortLimitDefaults.sort;
  const resolvedLimit = storedFields.limit != null
    ? storedFields.limit as number
    : storedFields.limit === null ? null : sortLimitDefaults.limit;
  const resolvedLimitOthers = storedFields.limitOthers != null
    ? Boolean(storedFields.limitOthers)
    : sortLimitDefaults.limitOthers;

  const chartFieldsConfig: ChartFieldsConfig = {
    sort: resolvedSort,
    limit: resolvedLimit,
    limitOthers: resolvedLimitOthers,
    ...(storedFields.sortField !== undefined ? { sortField: storedFields.sortField as string | null } : {}),
    ...(storedFields.sortDirection !== undefined ? { sortDirection: storedFields.sortDirection as 'asc' | 'desc' | null } : {}),
  };

  // ── Resolve chart style ───────────────────────────────────────────────
  // Migrate legacy 'auto' labelPosition to the chart-type default
  const rawLabelPos = stored.labelPosition as string | undefined;
  const resolvedLabelPosition: LabelPosition =
    !rawLabelPos || rawLabelPos === 'auto'
      ? getDefaultLabelPosition(ct)
      : rawLabelPos as LabelPosition;

  // Merge type-specific styles
  const storedTypeStyle = (stored.chartStyle ?? stored.typeStyle ?? {}) as TypeStyle;
  const defaults = getChartStyleDefaults(ct);

  const chartStyle: ChartStyleState = {
    ...defaults,
    // Spread all stored style properties (excluding non-style keys)
    ...(filterStyleProps(stored)),
    labelPosition: resolvedLabelPosition,
    typeStyle: { ...defaults.typeStyle, ...storedTypeStyle },
  };

  return {
    chartType: ct,
    chartStyle,
    chartFieldsConfig,
  };
}

/**
 * Extract persistence-ready objects from a `ChartEditorState`.
 *
 * Uses a **delta-save** strategy: only values that differ from the chart-type
 * defaults are written. This keeps the DB minimal and ensures future default
 * changes apply automatically to existing charts.
 *
 * @returns `{ chartStyle, chartFieldsConfig }` — two objects for the two DB columns
 */
export function toPersistedConfig(
  state: ChartEditorState,
  existingStyle: Record<string, unknown> | null | undefined,
  existingFieldsConfig: Record<string, unknown> | null | undefined,
): { chartStyle: Record<string, unknown>; chartFieldsConfig: Record<string, unknown> } {
  const ct = state.chartType;
  const defaultTypeStyle = DEFAULT_TYPE_STYLES[ct] ?? DEFAULT_TYPE_STYLES.bar;
  const sortLimitDefaults = DEFAULT_SORT_LIMIT[ct] ?? DEFAULT_SORT_LIMIT.bar;

  // ── Chart style delta ─────────────────────────────────────────────────
  const styleBase = { ...(existingStyle ?? {}) };

  // Write non-default common style properties
  const styleDefaults: Record<string, unknown> = { ...DEFAULT_CHART_STYLE };
  delete styleDefaults.typeStyle; // handled separately

  for (const [key, value] of Object.entries(state.chartStyle)) {
    if (key === 'typeStyle') continue;
    const defaultValue = styleDefaults[key];
    if (!deepEqual(value, defaultValue)) {
      styleBase[key] = value;
    } else {
      delete styleBase[key];
    }
  }

  // Write non-default type-specific style properties
  const typeStyleDelta = diffObject(
    state.chartStyle.typeStyle as Record<string, unknown>,
    defaultTypeStyle as Record<string, unknown>,
  );
  if (typeStyleDelta !== null) {
    // Persist as `chartStyle` key for backward compat with DB column shape
    styleBase.chartStyle = typeStyleDelta;
  } else {
    delete styleBase.chartStyle;
  }

  // ── Chart fields config delta ─────────────────────────────────────────
  const fieldsBase = { ...(existingFieldsConfig ?? {}) };
  const fieldsDefaults: Record<string, unknown> = {
    sort: sortLimitDefaults.sort,
    limit: sortLimitDefaults.limit,
    limitOthers: sortLimitDefaults.limitOthers,
  };

  for (const [key, value] of Object.entries(state.chartFieldsConfig)) {
    const defaultValue = fieldsDefaults[key];
    if (!deepEqual(value, defaultValue)) {
      fieldsBase[key] = value;
    } else {
      delete fieldsBase[key];
    }
  }

  return { chartStyle: styleBase, chartFieldsConfig: fieldsBase };
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Keys that are NOT chart style properties (stored elsewhere or used as sub-objects). */
const NON_STYLE_KEYS = new Set([
  'chartType', 'fields', 'sort', 'sortField', 'sortDirection', 'limit', 'limitOthers',
  'chartStyle', 'typeStyle', 'chartFieldsConfig',
]);

/** Filter a raw stored object to only include chart style properties. */
function filterStyleProps(raw: Record<string, unknown>): Partial<ChartStyleState> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!NON_STYLE_KEYS.has(key)) {
      result[key] = value;
    }
  }
  return result as Partial<ChartStyleState>;
}

/**
 * Return an object containing only keys whose values differ from `defaults`.
 * Returns `null` if all values match (i.e. completely default).
 */
function diffObject(
  obj: Record<string, unknown>,
  defaults: Record<string, unknown>,
): Record<string, unknown> | null {
  const delta: Record<string, unknown> = {};
  let hasDelta = false;

  for (const [key, value] of Object.entries(obj)) {
    if (!deepEqual(value, defaults[key])) {
      delta[key] = value;
      hasDelta = true;
    }
  }

  return hasDelta ? delta : null;
}

/**
 * Simple deep equality check (handles primitives, plain objects, arrays).
 * Sufficient for editor state values which are JSON-serialisable.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }

  if (typeof a === 'object') {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => deepEqual(aObj[key], bObj[key]));
  }

  return false;
}